/**
 * PartInfoTooltip Component
 * Quick preview tooltip shown on hover over the ℹ️ icon in Parts List.
 * 
 * UI Structure: Based on /app/memory/AI PARTS CLASSIFICATION/ai-classification-ui-v2.jsx
 * Data Mapping:
 *   - "What it does" → phase3.analysis.whatDoesItDo
 *   - "Primary Function" → phase3.analysis.primaryFunction
 *   - "Also:" (Secondary) → phase3.analysis.secondaryFunctions[]
 *   - "Specifics" → phase1.specifics[] (fallback)
 *   - Main Group → finalResult.mainGroupName
 *   - Sub-Group → finalResult.subGroupName OR phase3.recommendation.shelfLabel
 * 
 * v2 Updates (Jan 25, 2026):
 *   - Wider layout (480px) to reduce height
 *   - Max height (400px) with scrollable content area
 *   - Better content distribution
 */
import React from 'react';
import { Info, Sparkles, RefreshCw } from 'lucide-react';

// Tooltip dimensions - shared with HoverTooltipWrapper
export const TOOLTIP_WIDTH = 480;
export const TOOLTIP_MAX_HEIGHT = 400;

const PartInfoTooltip = ({ 
  part, 
  classificationData,
  mainGroupNames = {},
  onReclassify,
  isReclassifying = false 
}) => {
  // Check if classification data is available
  // Data can come from:
  // 1. Full classification (has phases with outputs)
  // 2. Cached classification (has finalResult but empty phases)
  const hasClassification = classificationData && (
    classificationData.finalResult || 
    Object.keys(classificationData.phases || {}).length > 0
  );
  
  if (!hasClassification) {
    // No classification data - show "Not Available" state
    return (
      <div 
        className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl"
        style={{ width: TOOLTIP_WIDTH }}
      >
        {/* Fixed Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-slate-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium text-sm truncate">{part?.description}</p>
              <p className="text-slate-500 text-xs">{part?.manufacturer_name} • {part?.mpn}</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <div className="p-3 bg-slate-700/50 rounded-lg mb-3">
            <p className="text-slate-400 text-sm">Classification data not available</p>
            <p className="text-slate-500 text-xs mt-1">
              This part was created before AI classification was implemented.
            </p>
          </div>
          
          {onReclassify && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReclassify();
              }}
              disabled={isReclassifying}
              className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm rounded-lg flex items-center justify-center gap-2 transition-colors"
            >
              {isReclassifying ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Classifying...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Run AI Classification
                </>
              )}
            </button>
          )}
          
          <p className="text-slate-500 text-xs mt-3 text-center">
            Click ℹ️ for full classification details
          </p>
        </div>
      </div>
    );
  }
  
  // Extract data from classification
  const phases = classificationData.phases || {};
  const phase3Output = phases.phase3?.output;
  const phase1Output = phases.phase1?.output;
  const finalResult = classificationData.finalResult;
  
  // Phase 3 analysis (primary data source for tooltip)
  const analysis = phase3Output?.analysis;
  const recommendation = phase3Output?.recommendation;
  
  // Data for display - prefer Phase 3 analysis, fallback to Phase 1 or finalResult
  const whatDoesItDo = analysis?.whatDoesItDo || phase1Output?.functionalEssence;
  const primaryFunction = analysis?.primaryFunction || phase1Output?.primaryFunction;
  // Secondary functions: prefer Phase 3 analysis, fallback to Phase 1
  const secondaryFunctions = analysis?.secondaryFunctions?.length > 0 
    ? analysis.secondaryFunctions 
    : (phase1Output?.secondaryFunctions || []);
  const specifics = phase1Output?.specifics || [];
  const searchTerms = analysis?.whoSearchesForThis;
  
  // Final classification - format: "R - Refrigeration Components" (code + name)
  const mainGroupCode = finalResult?.mainGroupCode || part?.main_group_code;
  // Look up the name from mainGroupNames (passed from PartsListPage), fallback to stored name
  const mainGroupName = mainGroupNames[mainGroupCode] || finalResult?.mainGroupName;
  // Only show "CODE - Name" format if we have a real name different from code
  const mainGroupDisplay = mainGroupCode && mainGroupName && mainGroupName !== mainGroupCode
    ? `${mainGroupCode} - ${mainGroupName}` 
    : mainGroupName || mainGroupCode;
  const subGroup = recommendation?.shelfLabel || finalResult?.subGroupName || part?.subgroup_name;
  
  return (
    <div 
      className="bg-slate-800 border border-slate-600 rounded-xl shadow-2xl flex flex-col"
      style={{ width: TOOLTIP_WIDTH, maxHeight: TOOLTIP_MAX_HEIGHT }}
    >
      {/* Fixed Header - Always visible */}
      <div className="p-4 border-b border-slate-700 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Info className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{part?.description}</p>
            <p className="text-slate-500 text-xs">{part?.manufacturer_name} • {part?.mpn}</p>
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {/* What it does */}
        {whatDoesItDo && (
          <div>
            <p className="text-slate-400 text-xs font-medium mb-1">What it does:</p>
            <p className="text-white text-sm leading-relaxed">{whatDoesItDo}</p>
          </div>
        )}

        {/* Primary Function */}
        {primaryFunction && (
          <div>
            <p className="text-slate-400 text-xs font-medium mb-1">Primary Function:</p>
            <p className="text-slate-300 text-sm">{primaryFunction}</p>
          </div>
        )}

        {/* Secondary Functions (Also:) */}
        {secondaryFunctions.length > 0 && (
          <div>
            <p className="text-slate-400 text-xs font-medium mb-1">Also:</p>
            <div className="flex flex-wrap gap-1">
              {secondaryFunctions.map((func, i) => (
                <span key={i} className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded">
                  {func}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Specifics */}
        {specifics.length > 0 && (
          <div>
            <p className="text-slate-400 text-xs font-medium mb-1">Specifics:</p>
            <p className="text-slate-300 text-xs leading-relaxed">
              {specifics.join(", ")}
            </p>
          </div>
        )}

        {/* Search Terms (if available) */}
        {searchTerms && (
          <div>
            <p className="text-slate-400 text-xs font-medium mb-1">Search terms:</p>
            <p className="text-slate-300 text-xs">{searchTerms}</p>
          </div>
        )}
      </div>

      {/* Fixed Footer - Classification Result */}
      <div className="p-4 border-t border-slate-700 flex-shrink-0 bg-slate-800/95">
        <div className="flex flex-wrap items-center gap-2">
          <div className="px-3 py-1.5 bg-slate-700 rounded text-xs">
            <span className="text-slate-400">Main:</span>
            <span className="text-white ml-1">{mainGroupDisplay}</span>
          </div>
          <span className="text-slate-500">→</span>
          <div className="px-3 py-1.5 bg-blue-900/40 border border-blue-500/30 rounded text-xs">
            <span className="text-blue-400">Sub:</span>
            <span className="text-white ml-1">{subGroup}</span>
          </div>
        </div>
        
        {/* Click for more hint */}
        <p className="text-slate-500 text-xs mt-3 text-center">
          Click ℹ️ for full classification details
        </p>
      </div>
    </div>
  );
};

export default PartInfoTooltip;
